import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useRef, useState } from 'react';
import { Button, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { SimpleModal, TableLoader, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import useStyles from './billNotificationSettingsModalStyles';

/**
 * Where a company wants bill-processing updates sent (LB-5831). Email is opt-in: every bill is always
 * visible in the Bills dashboard, so turning notifications off simply means "no emails, use the app".
 *
 * <p>Saved through the dedicated <c>PUT /companies/:id/bill-notifications</c> endpoint — a lightweight
 * toggle must not have to re-send and re-validate the whole company. The account-holder email is only
 * ever a *suggestion* (the field's placeholder / helper), never a silent send target: leaving the
 * field blank while on falls back to it, and turning the toggle off sends nothing.</p>
 */
// Client-side sanity check only; the backend's EmailAddress() validator is authoritative.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const BillNotificationSettingsModal = observer(_ref => {
  let isOpen = _ref.isOpen,
    handleOpen = _ref.handleOpen,
    handleClose = _ref.handleClose,
    trigger = _ref.trigger,
    config = _ref.config,
    companyId = _ref.companyId,
    loading = _ref.loading,
    onRetry = _ref.onRetry,
    onSaved = _ref.onSaved;
  const store = useStore();
  const classes = useStyles();
  const _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    enabled = _useState2[0],
    setEnabled = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    email = _useState4[0],
    setEmail = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    saving = _useState6[0],
    setSaving = _useState6[1];

  // Always the current selected company, so a save that resolves after a switch neither reports
  // against nor closes the newly-opened company's modal.
  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;

  // Re-seed from the latest config each time the modal opens (or the config loads), so a cancelled
  // edit does not stick and a retry's fresh config is reflected.
  useEffect(() => {
    if (isOpen) {
      var _config$enabled, _config$email;
      setEnabled((_config$enabled = config === null || config === void 0 ? void 0 : config.enabled) !== null && _config$enabled !== void 0 ? _config$enabled : false);
      setEmail((_config$email = config === null || config === void 0 ? void 0 : config.email) !== null && _config$email !== void 0 ? _config$email : '');
      setSaving(false);
    }
  }, [isOpen, config]);
  const accountHolderEmail = (config === null || config === void 0 ? void 0 : config.accountHolderEmail) || '';
  const trimmedEmail = email.trim();
  // A non-empty address must look like an email; blank is allowed (falls back to the account holder).
  const emailInvalid = enabled && trimmedEmail.length > 0 && !EMAIL_RE.test(trimmedEmail);
  const handleSave = async () => {
    if (emailInvalid) return;
    const savedCompanyId = companyId;
    // "Off" carries no address — send null even if a (possibly invalid) address is still in the field,
    // so turning notifications off can never be rejected by the backend's email validation.
    const outboundEmail = enabled ? trimmedEmail || null : null;
    setSaving(true);
    try {
      const resp = await store.company.updateBillNotificationsAsync.run({
        companyId: savedCompanyId,
        billNotificationsEnabled: enabled,
        billNotificationsEmail: outboundEmail
      });

      // If the company changed while the PUT was in flight, this save belonged to the previous one:
      // don't toast success, report onSaved, or close the newly selected company's modal.
      const isCurrent = savedCompanyId === companyIdRef.current;
      if (resp !== null && resp !== void 0 && resp.isSuccessful) {
        if (isCurrent) {
          toast('success', {
            body: ['Bill notification settings saved.']
          });
          onSaved === null || onSaved === void 0 || onSaved(savedCompanyId, {
            enabled,
            email: outboundEmail || ''
          });
          handleClose();
        }
      } else if (isCurrent && resp && resp.isSuccessful === false) {
        // Only a returned-but-unsuccessful response needs a toast, and only for the current company —
        // a stale save must not report failure under a newly selected one. A transport failure is
        // already surfaced by the async wrapper, so re-toasting it would double the error.
        toast('error', {
          body: ["Couldn't save bill notification settings. Please try again."]
        });
      }
    } catch (_unused) {
      // The async wrapper already surfaces a transport failure; swallow the rejection here so a
      // response-less error isn't an unhandled promise rejection (finally still resets the button).
    } finally {
      // Always clear saving — including when the wrapper rejects on a response-less transport error —
      // so Save/Cancel can't stay disabled for the rest of the modal session.
      setSaving(false);
    }
  };
  const renderBody = () => {
    if (loading) {
      return /*#__PURE__*/React.createElement(TableLoader, null);
    }

    // Config failed to load (a transient GET failure). Offer a retry rather than a permanently
    // unusable form — Save stays out of reach until the current config is known.
    if (!config) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        className: classes.hint
      }, "Couldn\u2019t load your current notification settings."), /*#__PURE__*/React.createElement("div", {
        className: classes.actions
      }, /*#__PURE__*/React.createElement(Button, {
        variant: "actionButtonInvert",
        onClick: () => onRetry === null || onRetry === void 0 ? void 0 : onRetry()
      }, "Retry"), /*#__PURE__*/React.createElement(Button, {
        variant: "actionButton",
        onClick: handleClose
      }, "Cancel")));
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormControlLabel, {
      control: /*#__PURE__*/React.createElement(Switch, {
        checked: enabled,
        onChange: event => setEnabled(event.target.checked),
        disabled: saving
      }),
      label: "Email me bill updates"
    }), enabled ? /*#__PURE__*/React.createElement(TextField, {
      className: classes.field,
      type: "email",
      label: "Notifications email",
      placeholder: accountHolderEmail || 'billing@yourcompany.com',
      value: email,
      onChange: event => setEmail(event.target.value),
      error: emailInvalid,
      helperText: emailInvalid ? 'Enter a valid email address.' : accountHolderEmail ? "Leave blank to use your account email (".concat(accountHolderEmail, ").") : 'Add an address to receive bill updates by email.',
      disabled: saving
    }) : /*#__PURE__*/React.createElement(Typography, {
      className: classes.hint
    }, "Bill updates won\u2019t be emailed \u2014 you\u2019ll still see every bill in the Bills dashboard."), /*#__PURE__*/React.createElement("div", {
      className: classes.actions
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "contained",
      onClick: handleSave,
      disabled: saving || emailInvalid
    }, saving ? 'Saving…' : 'Save'), /*#__PURE__*/React.createElement(Button, {
      variant: "actionButton",
      onClick: handleClose,
      disabled: saving
    }, "Cancel")));
  };
  return /*#__PURE__*/React.createElement(SimpleModal, {
    trigger: trigger,
    isOpen: isOpen,
    handleOpen: handleOpen,
    handleClose: handleClose
    // Its own buttons are the dialog's; the modal's footer would add a second Cancel beside them.
    ,
    noFooter: true,
    title: "Bill notifications",
    subTitle: "Choose where bill-processing updates are sent. Every bill is always visible in the Bills dashboard, whatever you pick here."
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.content
  }, renderBody()));
});